import React from 'react';
import { Bot, Zap, ExternalLink, Github } from 'lucide-react';
import { DUCKMON_AGENTS } from '../../types/agent';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const THEME = {
    purple: '#836EF9',
    purpleLight: '#A08BFF',
    purpleDark: '#5e17eb',
    yellow: '#FFD700',
    orange: '#FF8C00',
    green: '#22c55e',
    red: '#ef4444',
    blue: '#3b82f6',
    bg: 'linear-gradient(135deg, #5e17eb 0%, #3e0eb3 50%, #2a0a80 100%)',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(255, 215, 0, 0.2)',
    glow: '0 0 40px rgba(255, 215, 0, 0.15)',
};

const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
    } as React.CSSProperties,
    header: {
        textAlign: 'center' as const,
        marginBottom: '3rem',
    },
    title: {
        fontSize: '2.5rem',
        fontWeight: 800,
        color: '#fff',
        marginBottom: '1rem',
        background: `linear-gradient(135deg, ${THEME.purple}, ${THEME.yellow})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    } as React.CSSProperties,
    subtitle: {
        fontSize: '1.125rem',
        color: '#9ca3af',
        maxWidth: '600px',
        margin: '0 auto',
        lineHeight: 1.6,
    } as React.CSSProperties,
    agentGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '1.5rem',
        marginTop: '2rem',
    } as React.CSSProperties,
    agentCard: {
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '20px',
        padding: '2rem',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative' as const,
        overflow: 'hidden',
    } as React.CSSProperties,
    agentCardHover: {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 40px ${THEME.purple}40`,
    } as React.CSSProperties,
    agentHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        marginBottom: '1.5rem',
    } as React.CSSProperties,
    agentIcon: {
        fontSize: '3rem',
        width: '60px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '16px',
        background: `linear-gradient(135deg, ${THEME.purple}30, ${THEME.purple}10)`,
        border: `1px solid ${THEME.purple}40`,
    } as React.CSSProperties,
    agentInfo: {
        flex: 1,
    } as React.CSSProperties,
    agentName: {
        fontSize: '1.25rem',
        fontWeight: 700,
        color: '#fff',
        marginBottom: '0.25rem',
    } as React.CSSProperties,
    agentVersion: {
        fontSize: '0.75rem',
        color: '#9ca3af',
        fontWeight: 500,
    } as React.CSSProperties,
    statusBadge: (status: string) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        background: status === 'running' ? `${THEME.green}20` : status === 'beta' ? `${THEME.orange}20` : `${THEME.red}20`,
        border: `1px solid ${status === 'running' ? `${THEME.green}40` : status === 'beta' ? `${THEME.orange}40` : `${THEME.red}40`}`,
        color: status === 'running' ? THEME.green : status === 'beta' ? THEME.orange : THEME.red,
    } as React.CSSProperties),
    agentDescription: {
        fontSize: '0.875rem',
        color: '#d1d5db',
        lineHeight: 1.6,
        marginBottom: '1.5rem',
    } as React.CSSProperties,
    featuresList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '0.5rem',
        marginBottom: '1.5rem',
    },
    featureItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.5rem',
        fontSize: '0.8rem',
        color: '#9ca3af',
    } as React.CSSProperties,
    checkIcon: {
        color: THEME.green,
        marginTop: '0.125rem',
        flexShrink: 0,
    } as React.CSSProperties,
    indicatorTags: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '0.5rem',
        marginBottom: '1rem',
    },
    indicatorTag: {
        padding: '0.25rem 0.75rem',
        background: `${THEME.purple}15`,
        border: `1px solid ${THEME.purple}30`,
        borderRadius: '9999px',
        fontSize: '0.7rem',
        color: THEME.purpleLight,
        fontWeight: 600,
    } as React.CSSProperties,
    updateInterval: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.75rem',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '12px',
        fontSize: '0.8rem',
        color: '#9ca3af',
        marginTop: '1rem',
    } as React.CSSProperties,
    categoryBadge: (category: string) => ({
        position: 'absolute' as const,
        top: '1rem',
        right: '1rem',
        padding: '0.375rem 0.875rem',
        borderRadius: '9999px',
        fontSize: '0.7rem',
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        background: category === 'core' ? `${THEME.purple}20` : category === 'advanced' ? `${THEME.yellow}20` : `${THEME.orange}20`,
        border: `1px solid ${category === 'core' ? `${THEME.purple}40` : category === 'advanced' ? `${THEME.yellow}40` : `${THEME.orange}40`}`,
        color: category === 'core' ? THEME.purpleLight : category === 'advanced' ? THEME.yellow : THEME.orange,
    }),
};

// Status pulse animation
const PulseIcon = ({ status }: { status: string }) => (
    <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: status === 'running' ? THEME.green : status === 'beta' ? THEME.orange : THEME.red,
        animation: status === 'running' ? 'pulse 2s infinite' : 'none',
    }} />
);

// Check icon component
const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={styles.checkIcon}>
        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// ═══════════════════════════════════════════════════════════════════════════════
// AGENT CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

interface AgentCardProps {
    agent: typeof DUCKMON_AGENTS[0];
}

const AgentCard: React.FC<AgentCardProps> = ({ agent }) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const formatInterval = (seconds: number) => {
        if (seconds < 60) return `${seconds}s`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
        return `${Math.floor(seconds / 3600)}h`;
    };

    return (
        <div
            style={{
                ...styles.agentCard,
                ...(isHovered ? styles.agentCardHover : {}),
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Category Badge */}
            <div style={styles.categoryBadge(agent.category || 'core')}>
                {agent.category || 'CORE'}
            </div>

            {/* Header */}
            <div style={styles.agentHeader}>
                <div style={styles.agentIcon}>
                    {agent.icon || '🤖'}
                </div>
                <div style={styles.agentInfo}>
                    <div style={styles.agentName}>{agent.name}</div>
                    <div style={styles.agentVersion}>v{agent.version}</div>
                    <div style={{ marginTop: '0.5rem' }}>
                        <div style={styles.statusBadge(agent.status)}>
                            <PulseIcon status={agent.status} />
                            {agent.status.toUpperCase()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Description */}
            <p style={styles.agentDescription}>{agent.description}</p>

            {/* Features */}
            <div style={styles.featuresList}>
                {agent.features.slice(0, 4).map((feature, i) => (
                    <div key={i} style={styles.featureItem}>
                        <CheckIcon />
                        <span>{feature}</span>
                    </div>
                ))}
            </div>

            {/* Indicators */}
            <div style={styles.indicatorTags}>
                {agent.indicators.slice(0, 4).map((indicator, i) => (
                    <span key={i} style={styles.indicatorTag}>
                        {indicator}
                    </span>
                ))}
            </div>

            {/* Update Interval */}
            <div style={styles.updateInterval}>
                <Zap size={14} color={THEME.yellow} />
                <span>Updates every {formatInterval(agent.updateInterval)}</span>
                {agent.onChainEnabled && (
                    <>
                        <span style={{ color: '#4b5563' }}>•</span>
                        <span style={{ color: THEME.green, fontWeight: 600 }}>On-Chain</span>
                    </>
                )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const AdvancedAgents: React.FC = () => {
    const coreAgents = DUCKMON_AGENTS.filter(a => a.category === 'core');
    const advancedAgents = DUCKMON_AGENTS.filter(a => a.category === 'advanced');

    return (
        <div style={styles.container}>
            {/* Hero Section */}
            <div style={styles.header}>
                <h1 style={styles.title}>
                    🤖 DUCKMON AI Agent Suite
                </h1>
                <p style={styles.subtitle}>
                    7 specialized AI agents leveraging Monad's 10,000 TPS and 400ms block times
                    for institutional-grade market intelligence and automated trading strategies.
                </p>
                <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center', alignItems: 'center' }}>
                    <a
                        href="https://github.com/yeddevall/duckmon-agents-ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1.5rem',
                            background: `linear-gradient(135deg, ${THEME.purple}, ${THEME.purpleDark})`,
                            color: '#fff',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            textDecoration: 'none',
                            transition: 'all 0.2s',
                        }}
                    >
                        <Github size={18} />
                        View on GitHub
                        <ExternalLink size={14} />
                    </a>
                    <div style={{
                        padding: '0.75rem 1.5rem',
                        background: `${THEME.green}20`,
                        border: `1px solid ${THEME.green}40`,
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: THEME.green,
                    }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: THEME.green,
                            display: 'inline-block',
                            marginRight: '0.5rem',
                            animation: 'pulse 2s infinite',
                        }} />
                        Live on Monad Mainnet
                    </div>
                </div>
            </div>

            {/* Core Agents Section */}
            <section style={{ marginBottom: '4rem' }}>
                <div style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <Bot size={28} color={THEME.purple} />
                        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                            Core Trading Agents
                        </h2>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                        Essential agents for technical analysis, predictions, and market intelligence
                    </p>
                </div>
                <div style={styles.agentGrid}>
                    {coreAgents.map(agent => (
                        <AgentCard key={agent.id} agent={agent} />
                    ))}
                </div>
            </section>

            {/* Advanced Agents Section */}
            <section>
                <div style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <Zap size={28} color={THEME.yellow} />
                        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                            Advanced MEV & DeFi Agents
                        </h2>
                        <span style={{
                            padding: '0.25rem 0.75rem',
                            background: `${THEME.orange}20`,
                            border: `1px solid ${THEME.orange}40`,
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: THEME.orange,
                        }}>
                            BETA
                        </span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                        Cutting-edge agents for MEV opportunities, security analysis, and gas optimization
                    </p>
                </div>
                <div style={styles.agentGrid}>
                    {advancedAgents.map(agent => (
                        <AgentCard key={agent.id} agent={agent} />
                    ))}
                </div>
            </section>

            {/* Monad Advantages */}
            <section style={{ marginTop: '4rem', padding: '2rem', background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: '20px' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '0.5rem' }}>
                        Why Monad? ⚡
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                        Our agents leverage Monad's groundbreaking performance
                    </p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                    {[
                        { label: '10,000 TPS', value: 'Throughput', icon: '⚡', color: THEME.yellow },
                        { label: '400ms', value: 'Block Time', icon: '⏱️', color: THEME.purple },
                        { label: '800ms', value: 'Finality', icon: '✅', color: THEME.green },
                        { label: 'Parallel', value: 'Execution', icon: '🔀', color: THEME.blue },
                    ].map(stat => (
                        <div key={stat.label} style={{ textAlign: 'center', padding: '1.5rem', background: 'rgba(0,0,0,0.3)', borderRadius: '16px' }}>
                            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{stat.icon}</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: stat.color, marginBottom: '0.25rem' }}>
                                {stat.label}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {stat.value}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default AdvancedAgents;
